import React from 'react';

import { link, routes } from '@aline/routes';
import { Helmet } from 'react-helmet';
import { Navigate, Route, Routes, useMatch } from 'react-router-dom';
import { ContractTypesTab, RolesTab } from 'src/components';
import { GroupsPage, UsersList } from 'src/components/admin';
import { createRelativeRouter } from 'src/helpers/router-helpers';
import { makeStyles } from 'tss-react/mui';

import { AdminOnlyRoute } from './AdminOnlyRoute';
import { AdminSidebar } from './AdminSidebar';
import { AgentPersonalizationPage } from './agent-personalization/AgentPersonalizationPage';
import { SkillEditorPage } from './agent-personalization/SkillEditorPage';
import { ApiKeysPage } from './api-keys';
import { BillingPage } from './billing';
import { DataMigrationPage } from './data-migration/DataMigrationPage';
import { INTEGRATIONS_SECTION } from './integrations/integrations-nav-sections';
import { getIntegrationsSectionLink } from './integrations/integrations-section-spy';
import { IntegrationsList } from './IntegrationsList';
import { OrgDetails } from './org';
import { UpgradeRequestsPage } from './UpgradeRequestsPage';
import { UsagePage } from './usage';

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    minWidth: theme.breakpoints.values.md,
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'auto',
    padding: theme.spacing(1),
  },
  fullBleedContent: { padding: 0 },
}));

const { toRelativePath: toRelativeAdminPath } = createRelativeRouter(routes.AdminIndex, {
  nestedRoutes: true,
});

export function Admin(): JSX.Element {
  const { classes, cx } = useStyles();
  const isSkillsList = Boolean(useMatch(routes.AdminAgentSkillIndex));
  const isSkillEditor = Boolean(useMatch(routes.AdminAgentSkillDetail));

  return (
    <>
      <Helmet>
        <title>Admin</title>
      </Helmet>
      <div className={classes.container}>
        <AdminSidebar />
        <div
          className={cx(
            classes.content,
            (isSkillsList || isSkillEditor) && classes.fullBleedContent
          )}
        >
          <Routes>
            <Route
              element={
                <Navigate
                  replace
                  to={getIntegrationsSectionLink(INTEGRATIONS_SECTION.agentConnectors)}
                />
              }
              path={toRelativeAdminPath(routes.AdminAgentConnectors)}
            />
            <Route element={<OrgDetails />} path={toRelativeAdminPath(routes.AdminOrgIndex)} />
            <Route element={<ApiKeysPage />} path={toRelativeAdminPath(routes.AdminApiKeys)} />
            <Route element={<UsersList />} path={toRelativeAdminPath(routes.AdminUserIndex)} />
            <Route element={<GroupsPage />} path={toRelativeAdminPath(routes.AdminGroupIndex)} />
            <Route element={<RolesTab />} path={toRelativeAdminPath(routes.AdminRoles)} />
            <Route
              element={<ContractTypesTab />}
              path={toRelativeAdminPath(routes.AdminContractTypes)}
            />
            <Route
              element={<IntegrationsList />}
              path={toRelativeAdminPath(routes.AdminIntegrations)}
            />
            <Route
              element={<Navigate replace to={link.AdminAgentSkillIndex()} />}
              path={toRelativeAdminPath(routes.AdminAgentPersonalization)}
            />
            <Route
              element={<AgentPersonalizationPage />}
              path={toRelativeAdminPath(routes.AdminAgentSkillIndex)}
            />
            <Route
              element={<SkillEditorPage />}
              path={toRelativeAdminPath(routes.AdminAgentSkillDetail)}
            />
            <Route element={<BillingPage />} path={toRelativeAdminPath(routes.AdminBilling)} />
            <Route element={<UsagePage />} path={toRelativeAdminPath(routes.AdminUsage)} />
            {/* Admin-only pages: their server resolvers reject non-admins, so gate them here too. */}
            <Route element={<AdminOnlyRoute />}>
              <Route
                element={<UpgradeRequestsPage />}
                path={toRelativeAdminPath(routes.AdminUpgradeRequests)}
              />
              <Route
                element={<DataMigrationPage />}
                path={toRelativeAdminPath(routes.AdminDataMigration)}
              />
            </Route>
            <Route element={<Navigate replace to={link.AdminOrgIndex()} />} path="/" />
          </Routes>
        </div>
      </div>
    </>
  );
}
